import {
  useInstitutionLeadershipQuery,
  useInstitutionMemberMarketplaceRightsQuery,
} from "@/services/graphql/reactQuery";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PaxTab, Stack, Tabs, useTranslation } from "@ncino/pax-ui";
import Box from "@mui/material/Box";
import { z } from "zod";
import { PageHeader } from "@/shared/components/PageHeader";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { UsersAndPermissionsPanel } from "./-internal/UsersAndPermissionsPanel";
import { InstitutionPanel } from "./-internal/InstitutionPanel";
import { TermsAndConditionsPanel } from "./-internal/TermsAndConditionsPanel";
import {
  canViewSettings,
  isPermissionGroupMember,
  useCanViewTermsAndConditionsQuery,
} from "@/services/auth/permissions";
import { UnauthorizedError } from "@/shared/errors";
import { GearIcon } from "@phosphor-icons/react";

const SettingsTabs = z.enum(["users", "institution", "termsAndConditions"]);
type SettingsTabKey = z.infer<typeof SettingsTabs>;

export const Route = createFileRoute("/settings/")({
  component: RouteComponent,
  validateSearch: z.object({
    tab: SettingsTabs.catch("users"),
  }),
  beforeLoad: async ({ context }) => {
    const [canView] = await Promise.all([
      canViewSettings(context),
      isPermissionGroupMember("fullAccessAdmin", context),
    ]);
    if (!canView) {
      throw new UnauthorizedError();
    }
  },
  staticData: {
    breadcrumb: {
      title: ({ t }) => t("breadcrumbs.settings"),
      icon: GearIcon,
    },
  },
});

function RouteComponent() {
  const { t } = useTranslation("settings");
  const { institutionId } = Route.useRouteContext();
  const navigate = useNavigate({ from: "/settings/" });
  const { tab } = Route.useSearch();
  const canViewTermsAndConditions = useCanViewTermsAndConditionsQuery();

  const effectiveTab =
    tab === "termsAndConditions" && canViewTermsAndConditions.data !== true ? "users" : tab;

  const {
    data: leadershipData,
    isLoading: isLeadershipLoading,
    isError: isLeadershipError,
    refetch: refetchLeadership,
  } = useInstitutionLeadershipQuery(
    { institutionId: institutionId! },
    { enabled: institutionId != null }
  );

  const {
    data: memberRightsData,
    isLoading: isMembersLoading,
    isError: isMembersError,
    refetch: refetchMembers,
  } = useInstitutionMemberMarketplaceRightsQuery(
    { institutionId: institutionId! },
    { enabled: institutionId != null }
  );

  return (
    <Box>
      <PageHeader
        title={t("settings.pageHeader")}
        removeBottomPadding
        actionsBottom={
          <Stack direction="row" alignItems="center">
            <Tabs
              value={effectiveTab}
              aria-label={t("settings.settingsTabs")}
              onChange={(_, value: SettingsTabKey) => {
                navigate({ search: { tab: value } });
              }}
            >
              <PaxTab label={t("settings.usersAndPermissionsTab")} value="users" />
              <PaxTab label={t("settings.institutionTab")} value="institution" />
              {canViewTermsAndConditions.data === true && (
                <PaxTab label={t("settings.termsAndConditionsTab")} value="termsAndConditions" />
              )}
            </Tabs>
          </Stack>
        }
      />
      <PageContentContainer>
        {effectiveTab === "users" && (
          <UsersAndPermissionsPanel
            mpUsers={memberRightsData?.getInstitutionMemberRights ?? []}
            isLoading={institutionId == null || isMembersLoading}
            isError={isMembersError}
            refetch={refetchMembers}
          />
        )}
        {effectiveTab === "institution" && (
          <InstitutionPanel
            members={leadershipData?.getInstitutionLeadership ?? []}
            isLoading={institutionId == null || isLeadershipLoading}
            isError={isLeadershipError}
            refetch={refetchLeadership}
          />
        )}
        {effectiveTab === "termsAndConditions" && <TermsAndConditionsPanel />}
      </PageContentContainer>
    </Box>
  );
}
